import useSWR, { mutate } from "swr";
import { CONFIG } from "@/config-global";
import axios, { fetcher, endpoints } from "@/util/axios";

import type {
  WorkspaceDto,
  WorkspaceRole,
  UpdateWorkspaceDto,
  WorkspaceMemberDto,
  CreateWorkspaceDto,
  WorkspaceInvitationDto,
  WorkspaceSubscriptionDto,
  CreateWorkspaceInvitationData,
} from "./types";

// ----------------------------------------------------------------------
const WORKSPACE_ENDPOINT = `${CONFIG.serverUrl}${endpoints.workspace}`;

const ENDPOINTS = {
  GET_WORKSPACE_MEMBERS: "get-workspace-members",
  UPDATE_WORKSPACE: "update-workspace",
  UPDATE_MEMBER_ROLE: "update-member-role",
  REMOVE_WORKSPACE_MEMBER: "remove-workspace-member",
  GET_USER_WORKSPACE: "get-user-workspace",
  GET_USER_WORKSPACES: "get-user-workspaces",
  CREATE_WORKSPACE_INVITATION: "create-workspace-invitation",
  GET_WORKSPACE_INVITATIONS: "get-workspace-invitations",
  RESEND_WORKSPACE_INVITATION: "resend-workspace-invitation",
  REVOKE_WORKSPACE_INVITATION: "revoke-workspace-invitation",
  ACCEPT_INVITATION: "accept-invitation",
  GET_WORKSPACE_SUBSCRIPTION: "get-workspace-subscription",
  CREATE_WORKSPACE: "create-workspace",
};

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------

export function useGetWorkspaceMembers(workspaceId: string) {
  const url = workspaceId
    ? [
        `${WORKSPACE_ENDPOINT}`,
        {
          params: {
            id: workspaceId,
            endpoint: ENDPOINTS.GET_WORKSPACE_MEMBERS,
          },
        },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<{
    members: WorkspaceMemberDto[];
  }>(url, fetcher, swrOptions);

  return {
    members: data?.members || [],
    membersLoading: isLoading,
    membersError: error,
    membersValidating: isValidating,
    membersRefresh: mutate,
  };
}

// ----------------------------------------------------------------------

export function useGetUserWorkspace(userId: string) {
  const url = userId
    ? [
        `${WORKSPACE_ENDPOINT}`,
        { params: { userId, endpoint: ENDPOINTS.GET_USER_WORKSPACE } },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<{
    workspaces: WorkspaceDto[];
  }>(url, fetcher, swrOptions);

  return {
    workspace: data?.workspaces || [],
    workspaceLoading: isLoading,
    workspaceError: error,
    workspaceValidating: isValidating,
    workspaceRefresh: mutate,
  };
}

// ----------------------------------------------------------------------

export function useGetUserWorkspaces(userId: string) {
  const url = userId
    ? [
        `${WORKSPACE_ENDPOINT}`,
        { params: { endpoint: ENDPOINTS.GET_USER_WORKSPACES } },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<{
    workspaces: WorkspaceDto[];
  }>(url, fetcher, swrOptions);

  return {
    workspaces: data?.workspaces || [],
    workspacesLoading: isLoading,
    workspacesError: error,
    workspacesValidating: isValidating,
    workspacesRefresh: mutate,
  };
}

// ----------------------------------------------------------------------

export function useGetWorkspaceSubscription(workspaceId: string) {
  const url = workspaceId
    ? [
        `${WORKSPACE_ENDPOINT}`,
        {
          params: {
            id: workspaceId,
            endpoint: ENDPOINTS.GET_WORKSPACE_SUBSCRIPTION,
          },
        },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<{
    subscription: WorkspaceSubscriptionDto;
  }>(url, fetcher, swrOptions);

  return {
    subscription: data?.subscription,
    subscriptionLoading: isLoading,
    subscriptionError: error,
    subscriptionValidating: isValidating,
    subscriptionRefresh: mutate,
  };
}

// ----------------------------------------------------------------------

export function useGetWorkspaceInvitations(workspaceId: string) {
  const url = workspaceId
    ? [
        `${WORKSPACE_ENDPOINT}`,
        {
          params: {
            id: workspaceId,
            endpoint: ENDPOINTS.GET_WORKSPACE_INVITATIONS,
          },
        },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<{
    invitations: WorkspaceInvitationDto[];
  }>(url, fetcher, swrOptions);

  return {
    invitations: data?.invitations || [],
    invitationsLoading: isLoading,
    invitationsError: error,
    invitationsValidating: isValidating,
    invitationsRefresh: mutate,
  };
}

// ----------------------------------------------------------------------

export async function updateWorkspace(
  workspaceId: string,
  workspaceData: UpdateWorkspaceDto
) {
  const workspaceUrl = [
    `${WORKSPACE_ENDPOINT}`,
    { params: { endpoint: ENDPOINTS.GET_USER_WORKSPACE } },
  ];

  const workspacesUrl = [
    `${WORKSPACE_ENDPOINT}`,
    { params: { endpoint: ENDPOINTS.GET_USER_WORKSPACES } },
  ];

  // Server update
  const params = { id: workspaceId, endpoint: ENDPOINTS.UPDATE_WORKSPACE };
  const response = await axios.put(WORKSPACE_ENDPOINT, workspaceData, {
    params,
  });

  mutate(workspaceUrl);
  mutate(workspacesUrl);

  return response.data.workspace;
}

// ----------------------------------------------------------------------

export async function updateMemberRole(
  workspaceId: string,
  memberId: string,
  role: WorkspaceRole
) {
  const params = { id: workspaceId, endpoint: ENDPOINTS.UPDATE_MEMBER_ROLE };
  const response = await axios.put(
    WORKSPACE_ENDPOINT,
    { memberId, role },
    { params }
  );

  // Update members cache
  const membersCacheKey = [
    `${WORKSPACE_ENDPOINT}`,
    { params: { id: workspaceId, endpoint: ENDPOINTS.GET_WORKSPACE_MEMBERS } },
  ];

  await mutate(membersCacheKey);

  return response.data;
}

// ----------------------------------------------------------------------

export async function removeWorkspaceMember(
  workspaceId: string,
  memberId: string
) {
  const params = {
    id: workspaceId,
    memberId,
    endpoint: ENDPOINTS.REMOVE_WORKSPACE_MEMBER,
  };
  const response = await axios.delete(WORKSPACE_ENDPOINT, { params });

  // Update members cache
  const membersCacheKey = [
    `${WORKSPACE_ENDPOINT}`,
    { params: { id: workspaceId, endpoint: ENDPOINTS.GET_WORKSPACE_MEMBERS } },
  ];

  await mutate(membersCacheKey);

  return response.data;
}

// ----------------------------------------------------------------------

export async function createWorkspaceInvitation(
  workspaceId: string,
  invitationData: CreateWorkspaceInvitationData
) {
  const params = {
    id: workspaceId,
    endpoint: ENDPOINTS.CREATE_WORKSPACE_INVITATION,
  };

  // Optimistic cache update
  const cacheKey = [
    `${WORKSPACE_ENDPOINT}`,
    {
      params: {
        id: workspaceId,
        endpoint: ENDPOINTS.GET_WORKSPACE_INVITATIONS,
      },
    },
  ];

  const tempId = `temp-${Date.now()}`;
  const optimisticInvitation: WorkspaceInvitationDto = {
    id: tempId,
    workspaceId,
    email: invitationData.email,
    role: invitationData.role,
    message: invitationData.message,
    status: "PENDING",
    token: "",
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    inviter: invitationData.inviter,
  };

  mutate(
    cacheKey,
    (currentData: { invitations: WorkspaceInvitationDto[] } | undefined) => ({
      ...currentData,
      invitations: [optimisticInvitation, ...(currentData?.invitations || [])],
    }),
    false
  );

  try {
    const response = await axios.post(WORKSPACE_ENDPOINT, invitationData, {
      params,
    });

    // Update cache with server response
    mutate(
      cacheKey,
      (currentData: { invitations: WorkspaceInvitationDto[] } | undefined) => ({
        ...currentData,
        invitations: (currentData?.invitations || []).map((invitation) =>
          invitation.id === tempId ? response.data.invitation : invitation
        ),
      }),
      false
    );

    return response.data;
  } catch (error) {
    // Revert optimistic update on error
    mutate(
      cacheKey,
      (currentData: { invitations: WorkspaceInvitationDto[] } | undefined) => ({
        ...currentData,
        invitations: (currentData?.invitations || []).filter(
          (invitation) => invitation.id !== tempId
        ),
      }),
      false
    );
    throw error;
  }
}

// ----------------------------------------------------------------------

export async function resendWorkspaceInvitation(
  workspaceId: string,
  invitationIds: string[]
) {
  // Optimistic cache update
  const cacheKey = [
    `${WORKSPACE_ENDPOINT}`,
    {
      params: {
        id: workspaceId,
        endpoint: ENDPOINTS.GET_WORKSPACE_INVITATIONS,
      },
    },
  ];

  await mutate(
    cacheKey,
    (currentData: { invitations: WorkspaceInvitationDto[] } | undefined) => {
      if (!currentData) return currentData;

      const updatedInvitations = currentData.invitations.map((invitation) =>
        invitationIds.includes(invitation.id)
          ? {
              ...invitation,
              status: "PENDING" as const,
              updatedAt: new Date().toISOString(),
              expiresAt: new Date(
                Date.now() + 7 * 24 * 60 * 60 * 1000
              ).toISOString(),
            }
          : invitation
      );

      return { invitations: updatedInvitations };
    },
    false
  );

  try {
    const params = {
      id: workspaceId,
      endpoint: ENDPOINTS.RESEND_WORKSPACE_INVITATION,
    };

    const response = await axios.post(
      WORKSPACE_ENDPOINT,
      { invitationIds },
      { params }
    );
    return response.data;
  } catch (error) {
    // Revert cache on error
    await mutate(cacheKey);
    throw error;
  }
}

// ----------------------------------------------------------------------

export async function acceptInvitation(token: string) {
  const params = { endpoint: ENDPOINTS.ACCEPT_INVITATION };
  const response = await axios.post(WORKSPACE_ENDPOINT, { token }, { params });

  return response.data;
}

// ----------------------------------------------------------------------

export async function createWorkspace(workspaceData: CreateWorkspaceDto) {
  const params = { endpoint: ENDPOINTS.CREATE_WORKSPACE };

  try {
    const response = await axios.post(WORKSPACE_ENDPOINT, workspaceData, {
      params,
    });

    // Refresh the user workspaces cache
    const workspacesCacheKey = [
      `${WORKSPACE_ENDPOINT}`,
      { params: { endpoint: ENDPOINTS.GET_USER_WORKSPACES } },
    ];

    await mutate(workspacesCacheKey);

    return response.data.workspace;
  } catch (error) {
    console.error("Error creating workspace:", error);
    throw error;
  }
}

// ----------------------------------------------------------------------

export async function revokeWorkspaceInvitation(
  workspaceId: string,
  invitationId: string
) {
  const params = {
    id: workspaceId,
    invitationId,
    endpoint: ENDPOINTS.REVOKE_WORKSPACE_INVITATION,
  };

  // Optimistic cache update
  const cacheKey = [
    `${WORKSPACE_ENDPOINT}`,
    {
      params: {
        id: workspaceId,
        endpoint: ENDPOINTS.GET_WORKSPACE_INVITATIONS,
      },
    },
  ];

  mutate(
    cacheKey,
    (currentData: { invitations: WorkspaceInvitationDto[] } | undefined) => ({
      ...currentData,
      invitations: (currentData?.invitations || []).filter(
        (invitation) => invitation.id !== invitationId
      ),
    }),
    false
  );

  try {
    const response = await axios.delete(WORKSPACE_ENDPOINT, { params });
    return response.data;
  } catch (error) {
    // Revalidate cache on error
    mutate(cacheKey);
    throw error;
  }
}
